import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { formatApiError } from '../lib/errors';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
  Wallet, Plus, ArrowUpRight, ArrowDownLeft, CreditCard, TrendingUp, TrendingDown,
  Clock, CheckCircle, XCircle, Banknote, ArrowLeft, Gift, Users, Copy, Check, QrCode, Download, Eye, X, Share2, ChevronRight
} from 'lucide-react';
import QRCode from 'qrcode';
import { toast } from 'sonner';
import { buildInviteShareUrl } from '../lib/share';
import StripeConnectCard from '../components/StripeConnectCard';
import AiUsageCard from '../components/AiUsageCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TX_ICONS = {
  topup: ArrowDownLeft,
  purchase: ArrowUpRight,
  sale: ArrowDownLeft,
  premium: ArrowUpRight,
  catalog: ArrowUpRight,
  withdrawal: ArrowUpRight,
  refund: ArrowDownLeft,
  referral_bonus: Gift,
};

const TX_COLORS = {
  topup: 'text-green-600 bg-green-50',
  sale: 'text-green-600 bg-green-50',
  refund: 'text-green-600 bg-green-50',
  purchase: 'text-red-500 bg-red-50',
  premium: 'text-red-500 bg-red-50',
  catalog: 'text-red-500 bg-red-50',
  withdrawal: 'text-orange-500 bg-orange-50',
  referral_bonus: 'text-[#D97736] bg-[#FFF8F0]',
};

// Default KoLV logo for the QR centre. Drawn as a raster PNG (shapes + text only,
// no SVG image) so the QR <canvas> stays untainted and html2canvas can export it
// into the PDF — an SVG data-URL logo silently blanks the QR in the captured image.
function buildKolvLogo() {
  try {
    const c = document.createElement('canvas');
    c.width = 120; c.height = 120;
    const ctx = c.getContext('2d');
    const w = 120, h = 120, r = 28;
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(w - r, 0);
    ctx.quadraticCurveTo(w, 0, w, r);
    ctx.lineTo(w, h - r);
    ctx.quadraticCurveTo(w, h, w - r, h);
    ctx.lineTo(r, h);
    ctx.quadraticCurveTo(0, h, 0, h - r);
    ctx.lineTo(0, r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
    ctx.fillStyle = '#D97736';
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = '800 34px Arial, Helvetica, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('KoLV', w / 2, h / 2 + 2);
    return c.toDataURL('image/png');
  } catch (e) {
    return '';
  }
}
const KOLV_LOGO = buildKolvLogo();

// Printable-card colour themes, selectable before PDF download.
const CARD_THEMES = {
  orange: { labelLv: 'Oranžs', labelEn: 'Orange', swatch: '#D97736', headerBg: '#D97736', headerText: '#ffffff', cardBg: '#ffffff', title: '#121212', body: '#5A5A5A', badgeBg: '#FFF8F0', badgeBorder: 'rgba(217,119,54,0.35)', badgeText: '#D97736', footer: '#9A9A9A', qrBorder: '#E5E4E0' },
  dark:   { labelLv: 'Tumšs', labelEn: 'Dark', swatch: '#121212', headerBg: '#121212', headerText: '#ffffff', cardBg: '#1A1A1A', title: '#ffffff', body: '#B8B8B8', badgeBg: 'rgba(217,119,54,0.18)', badgeBorder: 'rgba(217,119,54,0.5)', badgeText: '#E89055', footer: '#8A8A8A', qrBorder: '#2E2E2E' },
  green:  { labelLv: 'Zaļš', labelEn: 'Green', swatch: '#16A34A', headerBg: '#16A34A', headerText: '#ffffff', cardBg: '#ffffff', title: '#121212', body: '#5A5A5A', badgeBg: '#F0FDF4', badgeBorder: 'rgba(22,163,74,0.35)', badgeText: '#15803D', footer: '#9A9A9A', qrBorder: '#E5E4E0' },
  cream:  { labelLv: 'Gaišs', labelEn: 'Cream', swatch: '#E8DCC4', headerBg: '#EADFCB', headerText: '#5A4631', cardBg: '#FBF8F2', title: '#3D2E1C', body: '#6B5A45', badgeBg: '#ffffff', badgeBorder: '#E0D4BE', badgeText: '#B06A2C', footer: '#A89673', qrBorder: '#E0D4BE' },
};

// Selectable card taglines — seasonal / event versions, plus a custom option.
const CARD_TAGLINES = {
  default: { labelLv: 'Pamata', labelEn: 'Default', title: 'Skenē un pievienojies!', body: 'Pērc un pārdod droši visā Latvijā — sludinājumi, veikali un pakalpojumi vienuviet.' },
  market:  { labelLv: 'Tirdziņš', labelEn: 'Market', title: 'Satiec mani tirdziņā!', body: 'Skenē un atrodi manas preces un sludinājumus KoLV — droši darījumi tiešsaistē.' },
  summer:  { labelLv: 'Vasara', labelEn: 'Summer', title: 'Vasaras tirgus KoLV!', body: 'Skenē un iepērcies vietējo tirdziņu piedāvājumos — svaigi, tuvu un droši.' },
  event:   { labelLv: 'Pasākums', labelEn: 'Event', title: 'Tiekamies pasākumā!', body: 'Skenē kodu, pievienojies KoLV un seko mūsu jaunumiem un piedāvājumiem.' },
  holiday: { labelLv: 'Svētki', labelEn: 'Holiday', title: 'Svētku piedāvājumi KoLV!', body: 'Skenē un atrodi īpašus svētku piedāvājumus no vietējiem pārdevējiem.' },
  custom:  { labelLv: 'Savs teksts', labelEn: 'Custom', title: '', body: '' },
};

// Print / share formats. A6/A5 share the ISO portrait ratio (same design);
// business card is landscape; story is a vertical 9:16 image for Instagram.
const CARD_FORMATS = {
  a6:       { labelLv: 'A6', labelEn: 'A6', w: 420, h: 592, prev: 0.72, layout: 'portrait', pdf: { format: [105, 148], orientation: 'portrait', pw: 105, ph: 148 } },
  a5:       { labelLv: 'A5', labelEn: 'A5', w: 420, h: 592, prev: 0.72, layout: 'portrait', pdf: { format: [148, 210], orientation: 'portrait', pw: 148, ph: 210 } },
  business: { labelLv: 'Vizītkarte', labelEn: 'Business card', w: 510, h: 330, prev: 0.72, layout: 'landscape', pdf: { format: [85, 55], orientation: 'landscape', pw: 85, ph: 55 } },
  story:    { labelLv: 'Stāsts', labelEn: 'Story', w: 405, h: 720, prev: 0.46, layout: 'story', pdf: { format: [95, 169], orientation: 'portrait', pw: 95, ph: 169 } },
};

// Story (9:16) background options — vibrant gradients behind the card content.
const STORY_BACKGROUNDS = {
  theme:  { labelLv: 'Tēma', labelEn: 'Theme', swatch: '#D97736', css: null },
  sunset: { labelLv: 'Saulriets', labelEn: 'Sunset', swatch: '#FF7E5F', css: 'linear-gradient(160deg,#FF7E5F 0%,#D9448A 55%,#5B2C83 100%)' },
  ocean:  { labelLv: 'Okeāns', labelEn: 'Ocean', swatch: '#2BC0E4', css: 'linear-gradient(160deg,#2BC0E4 0%,#1A6FB5 55%,#0B2A5B 100%)' },
  forest: { labelLv: 'Mežs', labelEn: 'Forest', swatch: '#3DAE5A', css: 'linear-gradient(160deg,#5BC27A 0%,#1B7A3E 55%,#0C3D23 100%)' },
  night:  { labelLv: 'Nakts', labelEn: 'Night', swatch: '#2B2B40', css: 'linear-gradient(160deg,#3A3A5C 0%,#1A1A2E 55%,#0D0D18 100%)' },
  warm:   { labelLv: 'Silts', labelEn: 'Warm', swatch: '#F9A826', css: 'linear-gradient(160deg,#FBC24A 0%,#D97736 55%,#A84C1E 100%)' },
};

export default function WalletPage() {
  const { user } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topupAmount, setTopupAmount] = useState('');
  const [topupLoading, setTopupLoading] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawIban, setWithdrawIban] = useState('');
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [showTopup, setShowTopup] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [topupSuccess, setTopupSuccess] = useState(false);
  const [recoveredAmount, setRecoveredAmount] = useState(0);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [cardTheme, setCardTheme] = useState('orange');
  const [customLogo, setCustomLogo] = useState(null);
  const [taglineKey, setTaglineKey] = useState('default');
  const [customTitle, setCustomTitle] = useState('');
  const [customBody, setCustomBody] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [cardFormat, setCardFormat] = useState('a6');
  const [storyBgKey, setStoryBgKey] = useState('theme');
  const [imgLoading, setImgLoading] = useState(false);
  const [shareImgLoading, setShareImgLoading] = useState(false);
  const cardRef = useRef(null);
  const logoInputRef = useRef(null);
  const logoSrc = customLogo || KOLV_LOGO;
  const theme = CARD_THEMES[cardTheme] || CARD_THEMES.orange;
  const tg = CARD_TAGLINES[taglineKey] || CARD_TAGLINES.default;
  const cardTitleText = taglineKey === 'custom' ? (customTitle.trim() || CARD_TAGLINES.default.title) : tg.title;
  const cardBodyText = taglineKey === 'custom' ? (customBody.trim() || CARD_TAGLINES.default.body) : tg.body;

  const fmt = CARD_FORMATS[cardFormat] || CARD_FORMATS.a6;
  const ownerName = user?.public_name || user?.name || '';

  const qrBlock = (size, logoPx) => (
    <div style={{ position: 'relative', width: `${size}px`, height: `${size}px` }}>
      {qrDataUrl && <img src={qrDataUrl} alt="QR" width={size} height={size} style={{ display: 'block' }} />}
      {logoSrc && (
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: `${logoPx}px`, height: `${logoPx}px`, background: '#ffffff', borderRadius: `${Math.round(logoPx * 0.2)}px`, padding: '3px', boxSizing: 'border-box' }}>
          <img src={logoSrc} alt="logo" style={{ width: '100%', height: '100%', borderRadius: `${Math.round(logoPx * 0.15)}px`, objectFit: 'cover', display: 'block' }} />
        </div>
      )}
    </div>
  );

  // Shared card body — portrait (A6/A5), landscape (business card) or story (9:16).
  const renderCardBody = () => {
    if (fmt.layout === 'story') {
      const sb = STORY_BACKGROUNDS[storyBgKey] || STORY_BACKGROUNDS.theme;
      const onGrad = !!sb.css;
      const bg = sb.css || theme.cardBg;
      const titleColor = onGrad ? '#ffffff' : theme.title;
      const bodyColor = onGrad ? 'rgba(255,255,255,0.92)' : theme.body;
      const footerColor = onGrad ? 'rgba(255,255,255,0.85)' : theme.footer;
      const sh = onGrad ? '0 1px 10px rgba(0,0,0,0.28)' : 'none';
      return (
        <div style={{ width: '100%', height: '100%', background: bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', padding: '48px 30px' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '50px', fontWeight: 800, color: titleColor, letterSpacing: '-1px', lineHeight: 1, textShadow: sh }}>KoLV</div>
            <div style={{ fontSize: '16px', color: bodyColor, marginTop: '8px', textShadow: sh }}>Latvijas tirgus tavā telefonā</div>
          </div>
          <div style={{ background: '#ffffff', padding: '20px', borderRadius: '26px', border: `2px solid ${onGrad ? 'rgba(255,255,255,0.6)' : theme.qrBorder}`, boxShadow: onGrad ? '0 12px 40px rgba(0,0,0,0.25)' : 'none' }}>
            {qrBlock(240, 60)}
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '30px', fontWeight: 800, color: titleColor, textShadow: sh }}>{cardTitleText}</div>
            <div style={{ fontSize: '16px', color: bodyColor, marginTop: '12px', lineHeight: 1.45, textShadow: sh }}>{cardBodyText}</div>
            <div style={{ marginTop: '20px', display: 'inline-block', background: onGrad ? 'rgba(255,255,255,0.95)' : theme.badgeBg, border: `1px solid ${onGrad ? 'rgba(255,255,255,0.7)' : theme.badgeBorder}`, borderRadius: '14px', padding: '11px 22px', color: onGrad ? '#B45309' : theme.badgeText, fontWeight: 700, fontSize: '18px' }}>
              Ielūguma kods: {referral?.code}
            </div>
            {ownerName && (
              <div style={{ marginTop: '14px', fontSize: '15px', color: bodyColor, textShadow: sh }}>
                {language === 'lv' ? 'Uzaicina' : language === 'ru' ? 'Приглашает' : 'Invited by'}: <span style={{ fontWeight: 700, color: titleColor }}>{ownerName}</span>
              </div>
            )}
          </div>
          <div style={{ color: footerColor, fontSize: '14px', letterSpacing: '0.5px', textShadow: sh }}>kolv.lv</div>
        </div>
      );
    }
    if (fmt.layout === 'landscape') {
      return (
        <div style={{ display: 'flex', width: '100%', height: '100%' }}>
          <div style={{ width: '45%', background: theme.headerBg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#ffffff', padding: '12px', borderRadius: '14px', border: `2px solid ${theme.qrBorder}` }}>
              {qrBlock(168, 42)}
            </div>
          </div>
          <div style={{ flex: 1, padding: '20px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <div style={{ fontSize: '32px', fontWeight: 800, color: theme.title, letterSpacing: '-0.5px', lineHeight: 1 }}>KoLV</div>
            <div style={{ fontSize: '17px', fontWeight: 700, color: theme.title, marginTop: '8px' }}>{cardTitleText}</div>
            <div style={{ fontSize: '12px', color: theme.body, marginTop: '6px', lineHeight: 1.35 }}>{cardBodyText}</div>
            <div style={{ marginTop: '12px', alignSelf: 'flex-start', background: theme.badgeBg, border: `1px solid ${theme.badgeBorder}`, borderRadius: '9px', padding: '6px 12px', color: theme.badgeText, fontWeight: 700, fontSize: '13px' }}>
              Ielūguma kods: {referral?.code}
            </div>
            {ownerName && (
              <div style={{ marginTop: '8px', fontSize: '12px', color: theme.body }}>
                {language === 'lv' ? 'Uzaicina' : language === 'ru' ? 'Приглашает' : 'Invited by'}: <span style={{ fontWeight: 700, color: theme.title }}>{ownerName}</span>
              </div>
            )}
            <div style={{ marginTop: '10px', color: theme.footer, fontSize: '11px', letterSpacing: '0.5px' }}>kolv.lv</div>
          </div>
        </div>
      );
    }
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ background: theme.headerBg, padding: '26px 0', textAlign: 'center' }}>
          <div style={{ color: theme.headerText, fontSize: '42px', fontWeight: 800, letterSpacing: '-1px', lineHeight: 1 }}>KoLV</div>
          <div style={{ color: theme.headerText, opacity: 0.92, fontSize: '15px', marginTop: '6px' }}>Latvijas tirgus tavā telefonā</div>
        </div>
        <div style={{ flex: 1, padding: '28px 26px 10px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 0 }}>
          <div style={{ display: 'inline-block', padding: '16px', background: '#ffffff', border: `2px solid ${theme.qrBorder}`, borderRadius: '20px' }}>
            {qrBlock(210, 54)}
          </div>
          <div style={{ marginTop: '20px', fontSize: '26px', fontWeight: 800, color: theme.title }}>{cardTitleText}</div>
          <div style={{ marginTop: '10px', fontSize: '15px', color: theme.body, lineHeight: 1.45, padding: '0 6px' }}>
            {cardBodyText}
          </div>
          <div style={{ marginTop: '16px', display: 'inline-block', background: theme.badgeBg, border: `1px solid ${theme.badgeBorder}`, borderRadius: '12px', padding: '9px 18px', color: theme.badgeText, fontWeight: 700, fontSize: '16px' }}>
            Ielūguma kods: {referral?.code}
          </div>
          {ownerName && (
            <div style={{ marginTop: '12px', fontSize: '14px', color: theme.body }}>
              {language === 'lv' ? 'Uzaicina' : language === 'ru' ? 'Приглашает' : 'Invited by'}: <span style={{ fontWeight: 700, color: theme.title }}>{ownerName}</span>
            </div>
          )}
        </div>
        <div style={{ padding: '0 0 16px', textAlign: 'center', color: theme.footer, fontSize: '13px', letterSpacing: '0.5px', flexShrink: 0 }}>kolv.lv</div>
      </div>
    );
  };
  const PREVIEW_SCALE = fmt.prev || 0.72;
  const cardOuterStyle = { width: `${fmt.w}px`, height: `${fmt.h}px`, background: theme.cardBg, fontFamily: "'Outfit', system-ui, Arial, sans-serif", position: 'relative', overflow: 'hidden' };

  // Generate the referral QR as a PNG data-URL. Rendered as an <img> (not a
  // live <canvas>) because html2canvas cannot reliably capture a <canvas> into
  // the PDF — it was producing a blank QR box.
  useEffect(() => {
    if (!referral?.link) { setQrDataUrl(''); return; }
    QRCode.toDataURL(referral.link, {
      errorCorrectionLevel: 'H',
      margin: 1,
      width: 640,
      color: { dark: '#121212', light: '#ffffff' },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(''));
  }, [referral?.link]);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const loadWallet = async () => {
      try {
        const { data } = await axios.get(`${API}/wallet`);
        setWallet(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    // Self-healing: ask the backend to re-check every pending Stripe top-up for
    // this user. This rescues payments where the user closed the tab before
    // landing on /wallet?topup=success and the Stripe webhook didn't fire.
    const reconcile = async () => {
      try {
        const { data } = await axios.post(`${API}/wallet/reconcile`, {});
        if (data?.total_credited > 0) {
          setRecoveredAmount(data.total_credited);
          setTimeout(() => setRecoveredAmount(0), 8000);
        }
      } catch (e) { /* silent — best-effort */ }
    };
    const loadReferral = async () => {
      try {
        const { data } = await axios.get(`${API}/referrals/me`);
        setReferral(data);
      } catch (e) { /* silent — best-effort */ }
    };
    loadWallet();
    loadReferral();
    reconcile().then(loadWallet);
    if (searchParams.get('topup') === 'success') {
      const sessionId = searchParams.get('session_id');
      if (sessionId) {
        axios.get(`${API}/payments/status/${sessionId}`)
          .then(() => {
            setTopupSuccess(true);
            setTimeout(() => setTopupSuccess(false), 5000);
            loadWallet();
          })
          .catch(e => console.error(e));
      }
    }
  }, [user, navigate, searchParams]);

  const fetchWallet = async () => {
    try {
      const { data } = await axios.get(`${API}/wallet`);
      setWallet(data);
    } catch (e) { console.error(e); }
  };

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) return;
    setTopupLoading(true);
    try {
      const { data } = await axios.post(`${API}/wallet/topup`, {
        amount,
        origin_url: window.location.origin
      });
      if (data.url) window.location.href = data.url;
    } catch (e) {
      console.error(e);
      alert(formatApiError(e, 'Kļūda'));
    } finally { setTopupLoading(false); }
  };

  const handleWithdraw = async () => {
    const amount = parseFloat(withdrawAmount);
    if (!amount || amount < 5) return;
    setWithdrawLoading(true);
    try {
      await axios.post(`${API}/wallet/withdraw`, {
        amount,
        iban: withdrawIban || null
      });
      setShowWithdraw(false);
      setWithdrawAmount('');
      setWithdrawIban('');
      fetchWallet();
    } catch (e) {
      alert(formatApiError(e, 'Kļūda'));
    } finally { setWithdrawLoading(false); }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('lv-LV', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const txLabel = (type) => {
    const labels = {
      topup: language === 'lv' ? 'Papildināšana' : language === 'ru' ? 'Пополнение' : 'Top-up',
      purchase: language === 'lv' ? 'Pirkums' : language === 'ru' ? 'Покупка' : 'Purchase',
      sale: language === 'lv' ? 'Pārdošana' : language === 'ru' ? 'Продажа' : 'Sale',
      premium: 'Premium',
      catalog: language === 'lv' ? 'Katalogs' : language === 'ru' ? 'Каталог' : 'Catalog',
      withdrawal: language === 'lv' ? 'Izmaksa' : language === 'ru' ? 'Вывод' : 'Withdrawal',
      refund: language === 'lv' ? 'Atgriešana' : language === 'ru' ? 'Возврат' : 'Refund',
      referral_bonus: language === 'lv' ? 'Referāļa bonuss' : language === 'ru' ? 'Реферальный бонус' : 'Referral bonus',
    };
    return labels[type] || type;
  };

  const copyReferral = async () => {
    if (!referral?.link) return;
    try {
      await navigator.clipboard.writeText(referral.link);
    } catch (e) {
      const ta = document.createElement('textarea');
      ta.value = referral.link; document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); } catch (_) {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = (platform) => {
    if (!referral?.link) return;
    const msg = language === 'lv'
      ? 'Pievienojies KoLV un sāc pirkt un pārdot! Reģistrējies caur manu saiti:'
      : language === 'ru'
      ? 'Присоединяйся к KoLV! Регистрируйся по моей ссылке:'
      : 'Join KoLV and start buying & selling! Sign up with my link:';
    // Share the OG invite URL (not the raw profile link) so Facebook / WhatsApp
    // render a rich, personalised preview card. It redirects to the inviter's
    // public profile with ?ref= attribution preserved.
    const link = referral.code ? buildInviteShareUrl(referral.code) : referral.link;
    let url = '';
    if (platform === 'whatsapp') url = `https://wa.me/?text=${encodeURIComponent(`${msg} ${link}`)}`;
    else if (platform === 'facebook') url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(link)}`;
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const onLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCustomLogo(reader.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const downloadCard = async () => {
    if (!referral?.link || !cardRef.current || pdfLoading) return;
    setPdfLoading(true);
    try {
      const [{ jsPDF }, html2canvas] = await Promise.all([
        import('jspdf'),
        import('html2canvas').then(m => m.default),
      ]);
      const canvas = await html2canvas(cardRef.current, { scale: 3, backgroundColor: '#ffffff', useCORS: true });
      const img = canvas.toDataURL('image/png');
      const doc = new jsPDF({ unit: 'mm', format: fmt.pdf.format, orientation: fmt.pdf.orientation });
      doc.addImage(img, 'PNG', 0, 0, fmt.pdf.pw, fmt.pdf.ph);
      doc.save(`kolv-ielugums-${referral.code || 'kartina'}-${cardFormat}.pdf`);
      toast.success(language === 'lv' ? 'PDF lejupielādēts' : language === 'ru' ? 'PDF загружен' : 'PDF downloaded');
    } catch (e) {
      toast.error(language === 'lv' ? 'Neizdevās lejupielādēt PDF' : language === 'ru' ? 'Не удалось загрузить PDF' : 'Could not download PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadImage = async () => {
    if (!referral?.link || !cardRef.current || imgLoading) return;
    setImgLoading(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, { scale: 3, backgroundColor: '#ffffff', useCORS: true });
      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = `kolv-ielugums-${referral.code || 'kartina'}-${cardFormat}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(language === 'lv' ? 'Attēls lejupielādēts' : language === 'ru' ? 'Изображение загружено' : 'Image downloaded');
    } catch (e) {
      toast.error(language === 'lv' ? 'Neizdevās lejupielādēt attēlu' : language === 'ru' ? 'Не удалось загрузить изображение' : 'Could not download image');
    } finally {
      setImgLoading(false);
    }
  };

  // Share the invite card as an image file via the native share sheet — on
  // phones this lets the user "Save to Photos" (and so add the QR to Apple /
  // Google wallet like a ticket) or send it straight to a friend. On desktop /
  // unsupported browsers it falls back to a PNG download.
  const shareCardImage = async () => {
    if (!referral?.link || !cardRef.current || shareImgLoading) return;
    setShareImgLoading(true);
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(cardRef.current, { scale: 3, backgroundColor: '#ffffff', useCORS: true });
      const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
      if (!blob) throw new Error('canvas-empty');
      const file = new File([blob], `kolv-ielugums-${referral.code || 'kartina'}.png`, { type: 'image/png' });
      const link = referral.link;
      const text = language === 'lv'
        ? `Pievienojies KoLV ar manu ielūgumu!\n${link}`
        : language === 'ru' ? `Присоединяйся к KoLV по моему приглашению!\n${link}` : `Join KoLV with my invite!\n${link}`;

      const saved = language === 'lv' ? 'Ielūguma kartiņa saglabāta' : language === 'ru' ? 'Карточка приглашения сохранена' : 'Invite card saved';

      // Try native share first (Safari / Android Chrome). If it is unsupported, OR
      // throws (e.g. Chrome loses the user gesture after html2canvas = NotAllowedError),
      // fall back to a plain PNG download so it NEVER silently fails.
      if (typeof navigator !== 'undefined' && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: 'KoLV', text });
          return; // shared (or user cancelled via AbortError below)
        } catch (err) {
          if (err && err.name === 'AbortError') return; // user dismissed the share sheet — do nothing
          // otherwise fall through to download
        }
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
      toast.success(saved);
    } catch (e) {
      toast.error(language === 'lv' ? 'Neizdevās saglabāt kartiņu' : language === 'ru' ? 'Не удалось сохранить карточку' : 'Could not save card');
    } finally {
      setShareImgLoading(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D97736]" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="wallet-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#5C5C5C] hover:text-[#121212] mb-6">
          <ArrowLeft className="w-5 h-5" />
          <span>{language === 'lv' ? 'Atpakaļ' : language === 'ru' ? 'Назад' : 'Back'}</span>
        </button>

        {topupSuccess && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-2xl flex items-center gap-3" data-testid="topup-success-msg">
            <CheckCircle className="w-5 h-5 text-green-600" />
            <span className="text-green-700 font-medium">
              {language === 'lv' ? 'Maciņš veiksmīgi papildināts!' : language === 'ru' ? 'Кошелёк успешно пополнен!' : 'Wallet topped up successfully!'}
            </span>
          </div>
        )}

        {recoveredAmount > 0 && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl flex items-start gap-3" data-testid="topup-recovered-msg">
            <CheckCircle className="w-5 h-5 text-emerald-600 mt-0.5" />
            <div className="text-emerald-700">
              <div className="font-medium">
                {language === 'lv'
                  ? `Atrasts neapstrādāts maksājums un automātiski ieskaitīts: €${recoveredAmount.toFixed(2)}`
                  : language === 'ru'
                  ? `Найден необработанный платёж и автоматически зачислен: €${recoveredAmount.toFixed(2)}`
                  : `Pending payment recovered and credited automatically: €${recoveredAmount.toFixed(2)}`}
              </div>
              <div className="text-sm text-emerald-600/80 mt-0.5">
                {language === 'lv'
                  ? 'Jūsu Stripe maksājums tika veiksmīgs, bet sākotnējais paziņojums netika piegādāts. Esam to izlabojuši.'
                  : language === 'ru'
                  ? 'Ваш платёж Stripe прошёл успешно, но первоначальное уведомление не было доставлено. Мы это исправили.'
                  : 'Your Stripe payment succeeded but the initial notification failed. We have now reconciled it.'}
              </div>
            </div>
          </div>
        )}

        {/* Balance Card */}
        <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-3xl p-8 mb-8 text-white" data-testid="wallet-balance-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-white/10 rounded-2xl flex items-center justify-center">
              <Wallet className="w-6 h-6" />
            </div>
            <div>
              <p className="text-white/60 text-sm">{language === 'lv' ? 'Jūsu maciņš' : language === 'ru' ? 'Ваш кошелёк' : 'Your Wallet'}</p>
              <p className="text-sm text-white/40">{user?.name}</p>
            </div>
          </div>
          <div className="mb-8">
            <p className="text-white/60 text-sm mb-1">{language === 'lv' ? 'Pieejamais atlikums' : language === 'ru' ? 'Доступный баланс' : 'Available Balance'}</p>
            <p className="text-5xl font-bold font-outfit" data-testid="wallet-balance">
              €{(wallet?.balance || 0).toFixed(2)}
            </p>
            {wallet?.pending_balance > 0 && (
              <p className="text-sm text-yellow-300/70 mt-1 flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {language === 'lv' ? 'Gaidāmā izmaksa' : language === 'ru' ? 'Ожидаемый вывод' : 'Pending withdrawal'}: €{wallet.pending_balance.toFixed(2)}
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <Dialog open={showTopup} onOpenChange={setShowTopup}>
              <DialogTrigger asChild>
                <Button className="flex-1 bg-white/15 hover:bg-white/25 text-white border-0 h-12" data-testid="topup-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  {language === 'lv' ? 'Papildināt' : language === 'ru' ? 'Пополнить' : 'Top Up'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{language === 'lv' ? 'Papildināt maciņu' : language === 'ru' ? 'Пополнить кошелёк' : 'Top Up Wallet'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="flex gap-2 flex-wrap">
                    {[5, 10, 25, 50, 100].map(a => (
                      <Button
                        key={a}
                        variant="outline"
                        onClick={() => setTopupAmount(a.toString())}
                        className={`${topupAmount === a.toString() ? 'border-[#D97736] bg-[#D97736]/5 text-[#D97736]' : 'border-[#E5E4E0]'}`}
                        data-testid={`topup-preset-${a}`}
                      >
                        €{a}
                      </Button>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'lv' ? 'Vai cita summa (€)' : language === 'ru' ? 'Или другая сумма (€)' : 'Or custom amount (€)'}</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10000"
                      step="0.01"
                      value={topupAmount}
                      onChange={e => setTopupAmount(e.target.value)}
                      placeholder="0.00"
                      className="h-12 text-lg bg-[#FAFAFA] border-[#E5E4E0]"
                      data-testid="topup-amount-input"
                    />
                  </div>
                  <p className="text-xs text-[#5C5C5C]">
                    {language === 'lv'
                      ? 'Stripe apstrādes maksa (~2.9% + €0.25) tiks atskaitīta.'
                      : language === 'ru'
                      ? 'Комиссия Stripe (~2.9% + €0.25) будет удержана.'
                      : 'Stripe processing fee (~2.9% + €0.25) will be deducted.'}
                  </p>
                  <Button
                    onClick={handleTopup}
                    disabled={topupLoading || !topupAmount || parseFloat(topupAmount) <= 0}
                    className="w-full h-12 bg-[#D97736] hover:bg-[#C06528] text-white"
                    data-testid="confirm-topup-btn"
                  >
                    <CreditCard className="w-5 h-5 mr-2" />
                    {topupLoading
                      ? (language === 'lv' ? 'Apstrādā...' : language === 'ru' ? 'Обработка...' : 'Processing...')
                      : (language === 'lv' ? `Papildināt €${topupAmount || '0'}` : language === 'ru' ? `Пополнить €${topupAmount || '0'}` : `Top Up €${topupAmount || '0'}`)}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={showWithdraw} onOpenChange={setShowWithdraw}>
              <DialogTrigger asChild>
                <Button
                  className="flex-1 bg-white/15 hover:bg-white/25 text-white border-0 h-12"
                  disabled={!wallet?.balance || wallet.balance < 5}
                  data-testid="withdraw-btn"
                >
                  <Banknote className="w-5 h-5 mr-2" />
                  {language === 'lv' ? 'Izmaksāt' : language === 'ru' ? 'Вывести' : 'Withdraw'}
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{language === 'lv' ? 'Pieprasīt izmaksu' : language === 'ru' ? 'Запросить вывод' : 'Request Withdrawal'}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="p-3 bg-[#F3F2EE] rounded-xl text-sm">
                    <span className="text-[#5C5C5C]">{language === 'lv' ? 'Pieejams' : language === 'ru' ? 'Доступно' : 'Available'}:</span>{' '}
                    <span className="font-bold text-[#121212]">€{(wallet?.balance || 0).toFixed(2)}</span>
                  </div>
                  <div className="space-y-2">
                    <Label>{language === 'lv' ? 'Summa (€)' : language === 'ru' ? 'Сумма (€)' : 'Amount (€)'}</Label>
                    <Input
                      type="number"
                      min="5"
                      max={wallet?.balance || 0}
                      step="0.01"
                      value={withdrawAmount}
                      onChange={e => setWithdrawAmount(e.target.value)}
                      placeholder="5.00"
                      className="h-11 bg-[#FAFAFA] border-[#E5E4E0]"
                      data-testid="withdraw-amount-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>IBAN</Label>
                    <Input
                      value={withdrawIban}
                      onChange={e => setWithdrawIban(e.target.value)}
                      placeholder="LV00HABA0000000000000"
                      className="h-11 bg-[#FAFAFA] border-[#E5E4E0]"
                      data-testid="withdraw-iban-input"
                    />
                  </div>
                  <p className="text-xs text-[#5C5C5C]">
                    {language === 'lv' ? 'Min. €5.00. Izmaksa tiek apstrādāta 1-3 darba dienu laikā.' : language === 'ru' ? 'Мин. €5.00. Обрабатывается в течение 1-3 рабочих дней.' : 'Min. €5.00. Processed within 1-3 business days.'}
                  </p>
                  <Button
                    onClick={handleWithdraw}
                    disabled={withdrawLoading || !withdrawAmount || parseFloat(withdrawAmount) < 5}
                    className="w-full h-12 bg-[#4A6B53] hover:bg-[#3a5a43] text-white"
                    data-testid="confirm-withdraw-btn"
                  >
                    {withdrawLoading
                      ? (language === 'lv' ? 'Apstrādā...' : language === 'ru' ? 'Обработка...' : 'Processing...')
                      : (language === 'lv' ? 'Pieprasīt izmaksu' : language === 'ru' ? 'Запросить вывод' : 'Request Withdrawal')}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stripe Connect — self-service payouts. Only shows for users who
            have wallet earnings; sellers without sales won't see it. */}
        {user?.is_seller && (
          <div className="mb-6">
            <StripeConnectCard
              walletBalance={wallet?.balance || 0}
              language={language}
              onWithdrawComplete={fetchWallet}
            />
          </div>
        )}

        {/* AI usage — seller-only mini panel that surfaces this month's AI
            call counts and a rough token-spend estimate. Helps sellers gauge
            their AI cost without leaving the dashboard. */}
        {user?.is_seller && (
          <div className="mb-6">
            <AiUsageCard />
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5" data-testid="stat-earned">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-xs text-[#5C5C5C]">{language === 'lv' ? 'Nopelnīts' : language === 'ru' ? 'Заработано' : 'Earned'}</span>
            </div>
            <p className="font-outfit text-xl font-bold text-[#121212]">€{(wallet?.total_earned || 0).toFixed(2)}</p>
          </div>
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5" data-testid="stat-spent">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown className="w-4 h-4 text-red-500" />
              <span className="text-xs text-[#5C5C5C]">{language === 'lv' ? 'Iztērēts' : language === 'ru' ? 'Потрачено' : 'Spent'}</span>
            </div>
            <p className="font-outfit text-xl font-bold text-[#121212]">€{(wallet?.total_spent || 0).toFixed(2)}</p>
          </div>
          <div className="bg-white border border-[#E5E4E0] rounded-2xl p-5" data-testid="stat-withdrawn">
            <div className="flex items-center gap-2 mb-2">
              <Banknote className="w-4 h-4 text-orange-500" />
              <span className="text-xs text-[#5C5C5C]">{language === 'lv' ? 'Izmaksāts' : language === 'ru' ? 'Выведено' : 'Withdrawn'}</span>
            </div>
            <p className="font-outfit text-xl font-bold text-[#121212]">€{(wallet?.total_withdrawn || 0).toFixed(2)}</p>
          </div>
        </div>

        {/* Commission info */}
        <div className="bg-[#FFF8F0] border border-[#D97736]/20 rounded-2xl p-5 mb-8">
          <p className="text-sm text-[#5C5C5C]">
            <strong className="text-[#D97736]">KoLV</strong>{' '}
            {language === 'lv'
              ? 'ņem 10% komisiju no katra pārdošanas darījuma. Stripe apstrādes maksa (~2.9% + €0.25) tiek atskaitīta papildinot maciņu.'
              : language === 'ru'
              ? 'берёт 10% комиссии с каждой продажи. Комиссия Stripe (~2.9% + €0.25) удерживается при пополнении кошелька.'
              : 'takes a 10% commission on each sale. Stripe processing fee (~2.9% + €0.25) is deducted when topping up.'}
          </p>
        </div>

        {/* Referral program */}
        <div className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden mb-8" data-testid="referral-card">
          <div className="bg-gradient-to-br from-[#D97736] to-[#C0612A] p-6 text-white">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
                <Gift className="w-5 h-5" />
              </div>
              <div>
                <h2 className="font-outfit font-semibold text-lg">
                  {language === 'lv' ? 'Uzaicini draugus — pelni 1%' : language === 'ru' ? 'Приглашай друзей — зарабатывай 1%' : 'Invite friends — earn 1%'}
                </h2>
                <p className="text-white/80 text-sm">
                  {language === 'lv'
                    ? `Pelni ${referral?.rate_percent ?? 1}% no katra uzaicinātā pārdevuma — pirmajiem ${referral?.order_limit ?? 100} pārdevumiem. Sedz KoLV.`
                    : language === 'ru'
                    ? `Зарабатывай ${referral?.rate_percent ?? 1}% с каждой продажи приглашённого — за первые ${referral?.order_limit ?? 100} продаж. Платит KoLV.`
                    : `Earn ${referral?.rate_percent ?? 1}% of every invitee's sale — for their first ${referral?.order_limit ?? 100} sales. Paid by KoLV.`}
                </p>
              </div>
            </div>
          </div>
          <div className="p-6 space-y-5">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 flex items-center bg-[#FAFAFA] border border-[#E5E4E0] rounded-xl px-4 h-12 overflow-hidden">
                <span className="text-sm text-[#121212] truncate" data-testid="referral-link">{referral?.link || '—'}</span>
              </div>
              <Button
                onClick={copyReferral}
                disabled={!referral?.link}
                className="h-12 bg-[#121212] hover:bg-[#2a2a2a] text-white px-5"
                data-testid="copy-referral-btn"
              >
                {copied ? <Check className="w-5 h-5 mr-2" /> : <Copy className="w-5 h-5 mr-2" />}
                {copied
                  ? (language === 'lv' ? 'Nokopēts' : language === 'ru' ? 'Скопировано' : 'Copied')
                  : (language === 'lv' ? 'Kopēt saiti' : language === 'ru' ? 'Копировать' : 'Copy link')}
              </Button>
            </div>

            <div className="flex items-center gap-2 flex-wrap" data-testid="referral-share-row">
              <span className="text-xs text-[#8A8A8A] mr-1">
                {language === 'lv' ? 'Dalīties vienā pieskārienā:' : language === 'ru' ? 'Поделиться в один клик:' : 'Share in one tap:'}
              </span>
              <button
                onClick={() => shareReferral('whatsapp')}
                disabled={!referral?.link}
                className="flex items-center gap-2 h-10 px-4 rounded-xl bg-[#25D366] hover:bg-[#1eb858] text-white text-sm font-medium transition-colors disabled:opacity-50"
                data-testid="share-whatsapp-btn"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </button>
              <button
                onClick={() => shareReferral('facebook')}
                disabled={!referral?.link}
                className="flex items-center gap-2 h-10 px-4 rounded-xl bg-[#1877F2] hover:bg-[#1465d8] text-white text-sm font-medium transition-colors disabled:opacity-50"
                data-testid="share-facebook-btn"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                Facebook
              </button>
              <button
                onClick={() => setShowQr((v) => !v)}
                disabled={!referral?.link}
                className="flex items-center gap-2 h-10 px-4 rounded-xl bg-[#121212] hover:bg-[#2a2a2a] text-white text-sm font-medium transition-colors disabled:opacity-50"
                data-testid="share-qr-btn"
              >
                <QrCode className="w-4 h-4" />
                {language === 'lv' ? 'QR kods' : language === 'ru' ? 'QR-код' : 'QR code'}
              </button>
            </div>

            {showQr && referral?.link && (
              <div
                className="relative flex flex-col items-center gap-4 bg-[#FAFAFA] border border-[#E5E4E0] rounded-xl p-6 pt-10 animate-in fade-in zoom-in-95 duration-200"
                data-testid="referral-qr"
              >
                <button
                  onClick={() => setShowQr(false)}
                  aria-label="Aizvērt"
                  className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-white border border-[#E5E4E0] text-[#5C5C5C] hover:bg-[#F0EFEB] hover:text-[#121212] transition-colors"
                  data-testid="close-qr-btn"
                >
                  <X className="w-4 h-4" />
                </button>
                {/* Live A6 card preview (the single QR) — reflects theme + logo, scan directly or print */}
                <div className="flex flex-col items-center gap-2" data-testid="card-preview">
                  <div
                    className="rounded-xl overflow-hidden shadow-lg ring-1 ring-black/5"
                    style={{ width: `${Math.round(fmt.w * PREVIEW_SCALE)}px`, height: `${Math.round(fmt.h * PREVIEW_SCALE)}px` }}
                  >
                    <div style={{ ...cardOuterStyle, transform: `scale(${PREVIEW_SCALE})`, transformOrigin: 'top left' }}>
                      {renderCardBody()}
                    </div>
                  </div>
                </div>

                <p className="text-sm text-[#5A5A5A] text-center max-w-xs">
                  {language === 'lv'
                    ? 'Skenē klātienē — tirdziņos un pasākumos. Kas noskenē un reģistrējas, kļūst par tavu uzaicināto.'
                    : language === 'ru'
                    ? 'Сканируй вживую — на рынках и мероприятиях. Кто отсканирует и зарегистрируется, станет вашим приглашённым.'
                    : 'Scan in person — at markets and events. Whoever scans and signs up becomes your invitee.'}
                </p>

                {/* Card theme chooser */}
                <div className="w-full" data-testid="card-theme-picker">
                  <p className="text-xs text-[#8A8A8A] mb-2 text-center">
                    {language === 'lv' ? 'Kartiņas noformējums' : language === 'ru' ? 'Оформление карточки' : 'Card design'}
                  </p>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    {Object.entries(CARD_THEMES).map(([key, t]) => (
                      <button
                        key={key}
                        onClick={() => setCardTheme(key)}
                        className={`flex items-center gap-2 h-9 px-3 rounded-lg border text-xs font-medium transition-colors ${cardTheme === key ? 'border-[#121212] bg-white' : 'border-[#E5E4E0] bg-white hover:border-[#C9C8C4]'}`}
                        data-testid={`card-theme-${key}`}
                      >
                        <span className="w-4 h-4 rounded-full border border-black/10" style={{ background: t.swatch }} />
                        {language === 'en' ? t.labelEn : t.labelLv}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tagline chooser — seasonal / event versions + custom */}
                <div className="w-full" data-testid="card-tagline-picker">
                  <p className="text-xs text-[#8A8A8A] mb-2 text-center">
                    {language === 'lv' ? 'Sauklis / teksts' : language === 'ru' ? 'Слоган / текст' : 'Tagline / text'}
                  </p>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    {Object.entries(CARD_TAGLINES).map(([key, t]) => (
                      <button
                        key={key}
                        onClick={() => setTaglineKey(key)}
                        className={`h-9 px-3 rounded-lg border text-xs font-medium transition-colors ${taglineKey === key ? 'border-[#121212] bg-white' : 'border-[#E5E4E0] bg-white hover:border-[#C9C8C4]'}`}
                        data-testid={`card-tagline-${key}`}
                      >
                        {language === 'en' ? t.labelEn : t.labelLv}
                      </button>
                    ))}
                  </div>
                  {taglineKey === 'custom' && (
                    <div className="mt-3 flex flex-col gap-2 max-w-sm mx-auto w-full" data-testid="card-custom-text">
                      <Input
                        value={customTitle}
                        onChange={(e) => setCustomTitle(e.target.value.slice(0, 40))}
                        placeholder={language === 'lv' ? 'Virsraksts (piem. Tiekamies Ziemas tirdziņā!)' : language === 'ru' ? 'Заголовок' : 'Title'}
                        className="bg-white"
                        data-testid="custom-title-input"
                      />
                      <textarea
                        value={customBody}
                        onChange={(e) => setCustomBody(e.target.value.slice(0, 140))}
                        rows={2}
                        placeholder={language === 'lv' ? 'Apraksts (īss teksts zem virsraksta)' : language === 'ru' ? 'Описание' : 'Description'}
                        className="w-full text-sm rounded-md border border-[#E5E4E0] bg-white px-3 py-2 resize-none focus:outline-none focus:ring-2 focus:ring-[#D97736]/30"
                        data-testid="custom-body-input"
                      />
                    </div>
                  )}
                </div>

                {/* Centre-logo choice */}
                <div className="w-full flex items-center justify-center gap-2 flex-wrap" data-testid="card-logo-picker">
                  <span className="text-xs text-[#8A8A8A]">{language === 'lv' ? 'Logo QR centrā:' : language === 'ru' ? 'Логотип в центре:' : 'Centre logo:'}</span>
                  <button
                    onClick={() => setCustomLogo(null)}
                    className={`h-9 px-3 rounded-lg border text-xs font-medium transition-colors ${!customLogo ? 'border-[#121212] bg-white' : 'border-[#E5E4E0] bg-white hover:border-[#C9C8C4]'}`}
                    data-testid="logo-kolv-btn"
                  >
                    KoLV
                  </button>
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    className={`h-9 px-3 rounded-lg border text-xs font-medium transition-colors ${customLogo ? 'border-[#121212] bg-white' : 'border-[#E5E4E0] bg-white hover:border-[#C9C8C4]'}`}
                    data-testid="logo-custom-btn"
                  >
                    {customLogo
                      ? (language === 'lv' ? 'Savs logo ✓' : language === 'ru' ? 'Свой логотип ✓' : 'Own logo ✓')
                      : (language === 'lv' ? 'Augšupielādēt savu' : language === 'ru' ? 'Загрузить свой' : 'Upload own')}
                  </button>
                  <input ref={logoInputRef} type="file" accept="image/*" onChange={onLogoPick} className="hidden" data-testid="logo-file-input" />
                </div>

                {/* Print format chooser */}
                <div className="w-full" data-testid="card-format-picker">
                  <p className="text-xs text-[#8A8A8A] mb-2 text-center">
                    {language === 'lv' ? 'Izmērs' : language === 'ru' ? 'Размер' : 'Size'}
                  </p>
                  <div className="flex items-center justify-center gap-2 flex-wrap">
                    {Object.entries(CARD_FORMATS).map(([key, f]) => (
                      <button
                        key={key}
                        onClick={() => setCardFormat(key)}
                        className={`h-9 px-3 rounded-lg border text-xs font-medium transition-colors ${cardFormat === key ? 'border-[#121212] bg-white' : 'border-[#E5E4E0] bg-white hover:border-[#C9C8C4]'}`}
                        data-testid={`card-format-${key}`}
                      >
                        {language === 'en' ? f.labelEn : f.labelLv}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Story background chooser — only for the 9:16 story format */}
                {cardFormat === 'story' && (
                  <div className="w-full" data-testid="story-bg-picker">
                    <p className="text-xs text-[#8A8A8A] mb-2 text-center">
                      {language === 'lv' ? 'Stāsta fons' : language === 'ru' ? 'Фон истории' : 'Story background'}
                    </p>
                    <div className="flex items-center justify-center gap-2 flex-wrap">
                      {Object.entries(STORY_BACKGROUNDS).map(([key, b]) => (
                        <button
                          key={key}
                          onClick={() => setStoryBgKey(key)}
                          title={language === 'en' ? b.labelEn : b.labelLv}
                          className={`w-9 h-9 rounded-full border-2 transition-transform hover:scale-105 ${storyBgKey === key ? 'border-[#121212] ring-2 ring-[#121212]/15' : 'border-white shadow-sm'}`}
                          style={{ background: b.css || b.swatch }}
                          data-testid={`story-bg-${key}`}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row items-center justify-center gap-2 w-full">
                  <Button
                    onClick={shareCardImage}
                    disabled={shareImgLoading}
                    className="h-11 w-full sm:w-auto bg-[#121212] hover:bg-[#2a2a2a] text-white px-6"
                    data-testid="share-card-image-btn"
                  >
                    <Share2 className="w-4 h-4 mr-2" />
                    {shareImgLoading
                      ? (language === 'lv' ? 'Gatavo…' : language === 'ru' ? 'Готовим…' : 'Preparing…')
                      : (language === 'lv' ? 'Dalīties / Saglabāt' : language === 'ru' ? 'Поделиться / Сохранить' : 'Share / Save')}
                  </Button>
                  <Button
                    onClick={downloadCard}
                    disabled={pdfLoading}
                    className="h-11 w-full sm:w-auto bg-[#D97736] hover:bg-[#C06528] text-white px-6"
                    data-testid="download-card-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {pdfLoading
                      ? (language === 'lv' ? 'Gatavo…' : language === 'ru' ? 'Готовим…' : 'Preparing…')
                      : (language === 'lv' ? 'PDF' : language === 'ru' ? 'PDF' : 'PDF')}
                  </Button>
                  <Button
                    onClick={downloadImage}
                    disabled={imgLoading}
                    variant="outline"
                    className="h-11 w-full sm:w-auto border-[#D97736] text-[#D97736] hover:bg-[#FFF8F0] px-6"
                    data-testid="download-image-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {imgLoading
                      ? (language === 'lv' ? 'Gatavo…' : language === 'ru' ? 'Готовим…' : 'Preparing…')
                      : (language === 'lv' ? 'Attēls (PNG)' : language === 'ru' ? 'Картинка (PNG)' : 'Image (PNG)')}
                  </Button>
                </div>
                <p className="text-xs text-[#8A8A8A] text-center max-w-xs" data-testid="wallet-save-hint">
                  {language === 'lv'
                    ? 'Padoms: nospied "Dalīties / Saglabāt" un saglabā kartiņu Fotoattēlos, lai QR vienmēr būtu pie rokas telefona makā — kā biļete.'
                    : language === 'ru'
                    ? 'Совет: нажми «Поделиться / Сохранить» и сохрани карточку в Фото, чтобы QR всегда был под рукой — как билет.'
                    : 'Tip: tap "Share / Save" and keep the card in your Photos so the QR is always handy — like a ticket in your wallet.'}
                </p>
              </div>
            )}

            {/* Off-screen printable A6 card captured to PDF — kept in DOM so the QR renders. */}
            {referral?.link && (
              <div
                ref={cardRef}
                aria-hidden="true"
                style={{ ...cardOuterStyle, position: 'fixed', left: '-9999px', top: 0 }}
              >
                {renderCardBody()}
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-xl p-4" data-testid="referral-visits">
                <div className="flex items-center gap-2 mb-1">
                  <Eye className="w-4 h-4 text-[#5C5C5C]" />
                  <span className="text-xs text-[#5C5C5C]">{language === 'lv' ? 'Atvērumi' : language === 'ru' ? 'Открытия' : 'Opens'}</span>
                </div>
                <p className="font-outfit text-xl font-bold text-[#121212]">{referral?.total_visits ?? 0}</p>
              </div>
              <div className="bg-[#FAFAFA] border border-[#E5E4E0] rounded-xl p-4" data-testid="referral-invited">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="w-4 h-4 text-[#5C5C5C]" />
                  <span className="text-xs text-[#5C5C5C]">{language === 'lv' ? 'Reģistrējušies' : language === 'ru' ? 'Зарегистрировались' : 'Signed up'}</span>
                </div>
                <p className="font-outfit text-xl font-bold text-[#121212]">{referral?.total_invited ?? 0}</p>
              </div>
              <div className="bg-[#FFF8F0] border border-[#D97736]/20 rounded-xl p-4" data-testid="referral-earned">
                <div className="flex items-center gap-2 mb-1">
                  <Gift className="w-4 h-4 text-[#D97736]" />
                  <span className="text-xs text-[#5C5C5C]">{language === 'lv' ? 'Nopelnīts' : language === 'ru' ? 'Заработано' : 'Earned'}</span>
                </div>
                <p className="font-outfit text-xl font-bold text-[#D97736]">€{(referral?.total_earned || 0).toFixed(2)}</p>
              </div>
            </div>

            {referral?.invitees?.length > 0 && (
              <div className="divide-y divide-[#E5E4E0] border border-[#E5E4E0] rounded-xl overflow-hidden" data-testid="referral-invitees">
                {referral.invitees.map(inv => (
                  <Link key={inv.id} to={`/lietotajs/${inv.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-[#F7F6F2] transition-colors" data-testid={`referral-invitee-${inv.id}`}>
                    <div className="w-9 h-9 rounded-full bg-[#F3F2EE] overflow-hidden flex items-center justify-center shrink-0">
                      {inv.avatar_url
                        ? <img src={inv.avatar_url} alt={inv.name} className="w-full h-full object-cover" />
                        : <span className="text-sm font-semibold text-[#5C5C5C]">{(inv.name || '?').charAt(0).toUpperCase()}</span>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-medium text-[#121212] truncate">{inv.name}</p>
                        <span className="text-[11px] font-medium text-[#8A8A8A] shrink-0 tabular-nums">{inv.sales_counted}/{inv.limit}</span>
                      </div>
                      <div className="mt-1.5 h-1.5 w-full bg-[#F0EFEB] rounded-full overflow-hidden" data-testid={`referral-progress-${inv.id}`}>
                        <div
                          className={`h-full rounded-full transition-all ${inv.sales_counted >= inv.limit ? 'bg-[#16A34A]' : 'bg-gradient-to-r from-[#D97736] to-[#E89055]'}`}
                          style={{ width: `${Math.min(100, (inv.sales_counted / inv.limit) * 100)}%`, minWidth: inv.sales_counted > 0 ? '4px' : '0' }}
                        />
                      </div>
                      <p className="text-[11px] text-[#8A8A8A] mt-1">
                        {inv.sales_counted >= inv.limit
                          ? (language === 'lv' ? 'Bonusa limits sasniegts' : language === 'ru' ? 'Лимит бонусов достигнут' : 'Bonus limit reached')
                          : (language === 'lv' ? `Vēl ${inv.limit - inv.sales_counted} bonusa pārdevumi` : language === 'ru' ? `Ещё ${inv.limit - inv.sales_counted} продаж с бонусом` : `${inv.limit - inv.sales_counted} bonus sales left`)}
                      </p>
                    </div>
                    <span className="text-sm font-semibold font-outfit text-[#D97736]">€{(inv.earned || 0).toFixed(2)}</span>
                    <ChevronRight className="w-4 h-4 text-[#C4C2BC] shrink-0" />
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Transactions */}
        <div className="bg-white border border-[#E5E4E0] rounded-2xl overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E5E4E0]">
            <h2 className="font-outfit font-semibold text-lg text-[#121212]">
              {language === 'lv' ? 'Darījumu vēsture' : language === 'ru' ? 'История транзакций' : 'Transaction History'}
            </h2>
          </div>
          {wallet?.transactions?.length > 0 ? (
            <div className="divide-y divide-[#E5E4E0]">
              {wallet.transactions.map(tx => {
                const Icon = TX_ICONS[tx.type] || Clock;
                const colorClass = TX_COLORS[tx.type] || 'text-gray-500 bg-gray-50';
                const isPositive = tx.amount > 0;
                return (
                  <div key={tx.id} className="flex items-center gap-4 px-6 py-4" data-testid={`tx-${tx.id}`}>
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#121212] truncate">{tx.description}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{txLabel(tx.type)}</Badge>
                        <span className="text-xs text-[#5C5C5C]">{formatDate(tx.created_at)}</span>
                      </div>
                    </div>
                    <div className={`text-right font-semibold font-outfit ${isPositive ? 'text-green-600' : 'text-[#121212]'}`}>
                      {isPositive ? '+' : ''}€{Math.abs(tx.amount).toFixed(2)}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <Clock className="w-10 h-10 mx-auto mb-3 text-[#5C5C5C] opacity-20" />
              <p className="text-[#5C5C5C]">{language === 'lv' ? 'Nav darījumu' : language === 'ru' ? 'Пока нет транзакций' : 'No transactions yet'}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
